import type { ReactNode } from 'react';
import { AlertCircle, Check, Clock, MessageSquare, Minus, Share2 } from 'lucide-react';
import { formatTimecode, type HotSpot, type LaneMark, type ReviewCellStatus } from '@vidmakr/shared';
import { cx } from './ui';

/**
 * Pieces shared by the project's Reviews page and an asset's review overview:
 * the colours of review sources, cell status icons, timeline lanes with
 * comment marks, and the hot-spot strip.
 */

/**
 * One colour per source (the team first, then links in the order they were
 * made). Neighbours differ in lightness as well as hue, so they stay apart
 * for colour-blind viewers and in greyscale.
 */
export const SOURCE_COLORS = ['#9b82ff', '#7dd3fc', '#e0703a', '#5eead4', '#d946ef', '#bef264', '#3b82f6', '#fda4af'];
export const sourceColor = (index: number) => SOURCE_COLORS[((index % SOURCE_COLORS.length) + SOURCE_COLORS.length) % SOURCE_COLORS.length];

/** Text colour of a status (always shown with an icon and a word, never colour alone). */
export const STATUS_TONE: Record<ReviewCellStatus, string> = {
  approved: 'text-ok',
  changes: 'text-warn',
  waiting: 'text-muted',
  shared: 'text-muted',
  no_decision: 'text-faint',
  comments: 'text-accent-2',
  not_shared: 'text-faint',
};

export function StatusIcon({ status, size = 14 }: { status: ReviewCellStatus; size?: number }) {
  const p = { size, 'aria-hidden': true, strokeWidth: 2.4 } as const;
  switch (status) {
    case 'approved':
      return <Check {...p} />;
    case 'changes':
      return <AlertCircle {...p} />;
    case 'waiting':
      return <Clock {...p} />;
    case 'shared':
      return <Share2 {...p} />;
    case 'comments':
      return <MessageSquare {...p} />;
    default:
      return <Minus {...p} />;
  }
}

export function SourceSwatch({ index, className }: { index: number; className?: string }) {
  return <span aria-hidden="true" className={cx('inline-block h-2 w-2 shrink-0 rounded-[2px]', className)} style={{ background: sourceColor(index) }} />;
}

/** "0:12" style labels under a lane, `count` ticks from 0 to the end. */
export function TimeRuler({ durationUs, count = 7, labelWidth }: { durationUs: number; count?: number; labelWidth: string }) {
  const ticks = Array.from({ length: count }, (_, i) => (durationUs * i) / (count - 1));
  // Short clips: tenths, so neighbouring ticks never read the same.
  const tenths = durationUs / (count - 1) < 2_000_000;
  return (
    <div className="flex gap-3" aria-hidden="true">
      <div className={cx('shrink-0', labelWidth)} />
      <div className="flex flex-1 justify-between font-mono text-[11px] text-muted">
        {ticks.map((t, i) => (
          <span key={i} className={cx(i > 0 && i < count - 1 && i % 2 === 1 && 'hidden sm:inline')}>
            {shortTime(t, tenths)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** m:ss for rulers. */
export function shortTime(us: number, tenths = false): string {
  if (tenths) {
    const d = Math.max(0, Math.round(us / 1e5));
    const s = Math.floor(d / 10);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}.${d % 10}`;
  }
  const s = Math.max(0, Math.round(us / 1e6));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** A source's lane: points as dots, ranges as bars; marks are buttons that focus their thread. */
export function Lane({
  label,
  colorIndex,
  marks,
  dim,
  focused,
  onPick,
  titleOf,
  labelWidth,
  height = 28,
  sourceId,
  extra,
}: {
  label: ReactNode;
  colorIndex: number;
  marks: readonly LaneMark[];
  dim?: boolean;
  focused?: string | null;
  onPick?: (threadId: string) => void;
  titleOf: (threadId: string) => string;
  labelWidth: string;
  height?: number;
  sourceId: string;
  extra?: ReactNode;
}) {
  const color = sourceColor(colorIndex);
  return (
    <div className="flex items-center gap-3" data-review-lane={sourceId} data-dim={dim || undefined}>
      <div className={cx('flex min-w-0 shrink-0 items-center gap-2 text-xs', labelWidth, dim ? 'text-faint' : 'text-fg')}>
        <SourceSwatch index={colorIndex} />
        <span className="truncate" title={typeof label === 'string' ? label : undefined}>{label}</span>
      </div>
      <div className="relative flex-1 rounded-md border border-line bg-panel-2" style={{ height }}>
        {marks.map((m) => {
          const isFocused = focused === m.threadId;
          const title = titleOf(m.threadId);
          const common = {
            title,
            'aria-label': title,
            'aria-pressed': isFocused,
            onClick: () => onPick?.(m.threadId),
            'data-lane-mark': m.threadId,
          };
          return m.end !== undefined ? (
            <button
              key={m.threadId}
              {...common}
              className={cx('absolute rounded focus-visible:outline-2 focus-visible:outline-white', isFocused && 'ring-2 ring-white')}
              style={{
                top: height / 2 - 7,
                left: `${m.start * 100}%`,
                width: `max(6px, ${(m.end - m.start) * 100}%)`,
                height: 14,
                background: color,
                opacity: dim ? 0.3 : m.resolved ? 0.45 : 0.85,
              }}
            />
          ) : (
            <button
              key={m.threadId}
              {...common}
              className={cx('absolute rounded-full border-2 focus-visible:outline-2 focus-visible:outline-white', isFocused ? 'border-white' : 'border-panel')}
              style={{ top: height / 2 - 8, left: `calc(${m.start * 100}% - 8px)`, width: 16, height: 16, background: color, opacity: dim ? 0.3 : m.resolved ? 0.5 : 1 }}
            />
          );
        })}
        {extra}
      </div>
    </div>
  );
}

/** The strip under the lanes: where several comments land on the same stretch. */
export function HotSpotStrip({ spots, durationUs, labelWidth, fps }: { spots: readonly HotSpot[]; durationUs: number; labelWidth: string; fps: number }) {
  return (
    <div className="flex items-center gap-3" data-hot-spots={spots.length}>
      <div className={cx('shrink-0 text-xs text-muted', labelWidth)}>Hot spots</div>
      <div className="relative h-2.5 flex-1 rounded-full bg-panel-2">
        {spots.map((h, i) => (
          <span
            key={i}
            role="img"
            aria-label={`${h.count} comments from ${h.sources} ${h.sources === 1 ? 'source' : 'sources'} around ${formatTimecode(h.startUs, fps)}`}
            title={`${h.count} comments · ${h.sources} ${h.sources === 1 ? 'source' : 'sources'} · ${shortTime(h.startUs)}–${shortTime(h.endUs)}`}
            className="absolute top-0 h-2.5 rounded-full bg-danger"
            style={{
              left: `${(h.startUs / durationUs) * 100}%`,
              width: `${Math.max(1, ((h.endUs - h.startUs) / durationUs) * 100)}%`,
              opacity: Math.min(1, 0.25 + 0.25 * h.count),
            }}
            data-hot-spot={h.count}
          />
        ))}
      </div>
    </div>
  );
}

/** Pill naming a source. */
export function SourcePill({ name, colorIndex }: { name: string; colorIndex: number }) {
  return (
    <span className="inline-flex max-w-[12rem] items-center gap-1.5 rounded-full border border-line bg-panel-2 px-2 py-0.5 text-[11px]">
      <SourceSwatch index={colorIndex} />
      <span className="truncate">{name}</span>
    </span>
  );
}
